import { useState, type RefObject } from "react";
import { GlassCard, ScreenShell, SimHeader } from "./shared";
import { InkSplash } from "./decor";

export interface OpenTextData {
  decisionNumber: number;
  totalDecisions: number;
  type: string;
  competency: string;
  scenario: string;
  prompt: string;
  helper: string;
  minChars: number;
}

export function AnswerOpenTextScreen({
  data,
  initialValue = "",
  xp = 0,
  timerDisplay,
  textareaRef,
  onValueChange,
  onSubmit,
  scoring = false,
  error,
  blockMsg,
  rejectionMessage,
  onPaste,
  onCopy,
  onCut,
  onDrop,
  onContextMenu,
}: {
  data: OpenTextData;
  initialValue?: string;
  xp?: number;
  timerDisplay?: string;
  textareaRef?: RefObject<HTMLTextAreaElement>;
  onValueChange?: (v: string) => void;
  onSubmit?: (text: string) => void;
  scoring?: boolean;
  error?: string | null;
  blockMsg?: string | null;
  rejectionMessage?: string | null;
  onPaste?: React.ClipboardEventHandler<HTMLTextAreaElement>;
  onCopy?: React.ClipboardEventHandler<HTMLTextAreaElement>;
  onCut?: React.ClipboardEventHandler<HTMLTextAreaElement>;
  onDrop?: React.DragEventHandler<HTMLTextAreaElement>;
  onContextMenu?: React.MouseEventHandler<HTMLTextAreaElement>;
}) {
  const [value, setValue] = useState(initialValue);
  const progress = (data.decisionNumber / data.totalDecisions) * 100;
  const meetsMin = value.length >= data.minChars;

  function handleChange(v: string) {
    setValue(v);
    onValueChange?.(v);
  }

  return (
    <ScreenShell>
      <SimHeader decision={data.decisionNumber} xp={xp} timerDisplay={timerDisplay} />
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 pb-12 md:grid-cols-[38%_62%] md:px-8">
        {/* LEFT */}
        <div className="flex flex-col gap-3.5">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/45">
            Decision {data.decisionNumber} of {data.totalDecisions}
          </div>
          <div className="h-[3px] w-full overflow-hidden rounded-full bg-white/5">
            <div className="h-full transition-all duration-700" style={{ width:`${progress}%`, background:"linear-gradient(90deg, #8B5CF6, #22D3EE)" }} />
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="rounded-full border px-3 py-1 text-[11px] font-medium"
              style={{ background:"rgba(34,211,238,0.15)", borderColor:"rgba(34,211,238,0.4)", color:"#22D3EE" }}>
              {data.type}
            </span>
            <span className="rounded-full border px-3 py-1 text-[11px] font-medium"
              style={{ background:"rgba(167,139,250,0.15)", borderColor:"rgba(167,139,250,0.4)", color:"#A78BFA" }}>
              {data.competency}
            </span>
          </div>
          <GlassCard className="p-5">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/40">The Situation</div>
            <div className="text-[14px] leading-[1.7]" style={{ color:"#CBD5E1" }}>{data.scenario}</div>
          </GlassCard>
        </div>

        {/* RIGHT */}
        <div className="flex flex-col gap-3 md:pt-[1.75rem]">
          {rejectionMessage && (
            <div className="rounded-xl border border-amber-400/40 bg-amber-400/10 px-4 py-3 text-sm text-amber-200">
              {rejectionMessage}
            </div>
          )}
          <div className="text-[14px] font-bold text-white">{data.prompt}</div>
          <div className="text-[12px] text-white/55">{data.helper}</div>
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              placeholder="Type your response here… your own words, in full sentences."
              spellCheck={false}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              disabled={scoring}
              onPaste={onPaste}
              onCopy={onCopy}
              onCut={onCut}
              onDrop={onDrop}
              onContextMenu={onContextMenu}
              className={`min-h-[220px] w-full resize-y rounded-2xl border p-4 text-[15px] leading-[1.6] text-white outline-none transition-colors disabled:opacity-60 select-text ${blockMsg ? "border-rose-400/80 ring-2 ring-rose-400/40" : ""}`}
              style={{ background:"rgba(255,255,255,0.04)", borderColor:blockMsg?"":"rgba(255,255,255,0.08)", WebkitUserSelect:"text", userSelect:"text" }}
              onFocus={(e) => { e.currentTarget.style.borderColor = "rgba(139,92,246,0.6)"; e.currentTarget.style.boxShadow = "0 0 0 4px rgba(139,92,246,0.12)"; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; e.currentTarget.style.boxShadow = "none"; }}
            />
            {blockMsg && (
              <div className="mt-1 text-right text-[12px] text-rose-300">{blockMsg}</div>
            )}
          </div>
          <div className="flex items-center justify-between text-[12px]">
            <span style={{ color:meetsMin?"#10B981":"rgba(255,255,255,0.45)" }}>
              {meetsMin ? "✓" : ""} {value.length} / {data.minChars} minimum
            </span>
          </div>
          {error && <p className="text-sm text-rose-300">{error}</p>}
          <button disabled={!meetsMin || scoring} onClick={() => onSubmit?.(value)}
            className="relative mt-1 overflow-hidden rounded-2xl py-4 text-[15px] font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-50"
            style={{ background:"linear-gradient(90deg, #e879f9, #8B5CF6, #22D3EE)", boxShadow:"0 4px 24px rgba(139,92,246,0.4)" }}>
            {scoring
              ? <span className="relative z-10 inline-flex items-center gap-2"><Spinner /> Scoring your response…</span>
              : <span className="relative z-10">Submit Response →</span>}
            <span className="pointer-events-none absolute inset-0"
              style={{ background:"linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)", backgroundSize:"200% 100%", animation:"shimmer 2.4s linear infinite" }} />
          </button>
          <div className="text-center text-[12px] text-white/40">🔒 Scores are not shown during the simulation</div>
          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <HintCard text="Specific actions, not vague intentions" />
            <HintCard text="Names the people and context involved" />
          </div>
          <InkSplash />
        </div>
      </div>
    </ScreenShell>
  );
}

function HintCard({ text }: { text: string }) {
  return (
    <div className="rounded-xl border px-3 py-2 text-[11px] text-white/45 backdrop-blur"
      style={{ background:"rgba(255,255,255,0.025)", borderColor:"rgba(255,255,255,0.06)" }}>
      <span style={{ color:"#10B981" }}>✓</span> {text}
    </div>
  );
}

function Spinner() {
  return <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />;
}
